import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader.js';

/**
 * Everything between the scene and the screen on the high setting: the glow, the grade,
 * the tone curve and the antialiasing.
 *
 * The frame is drawn into a half-float target, because the grade and the tone curve both
 * want the light as it actually is — a sunlit wall at 1.4, the sun's disc at 6 — rather than
 * clipped to 1 at the first chance. The target has **one** sample. It had four once, and
 * that was nearly all of a night frame's cost (DECISIONS.md §69): four samples of eight bytes
 * a pixel to write and resolve at four megapixels, and the bloom's blend back into it paying
 * for a second resolve. FXAA runs last instead, after the sRGB encode, where it expects its
 * input.
 *
 * The grade is folded into `OutputPass` rather than given a pass of its own: it is a few
 * multiplies per pixel, and a separate pass would be a full-screen read and write to do them.
 * It is applied before the tone curve, in linear light, which is where exposure, white
 * balance and a contrast pivoted on mid-grey mean what they say.
 *
 * The bloom only ever catches what is *above* white — the sun, a lamp head, a lit window at
 * night — so by day it is the sun's glare and a glint off a windscreen, not a soft-focus
 * filter over the whole city.
 */

export interface Grade {
  /** Linear multiplier before everything else. */
  exposure: number;
  /** Per-channel multiplier: warm for golden hour, cool for night. */
  balance: [number, number, number];
  /** Applied to luminance, pivoted at 0.18 so mid-grey stays where it is. */
  contrast: number;
  saturation: number;
  /** How far the corners darken, 0–1. */
  vignette: number;
  bloomStrength: number;
  /** In linear light, after exposure. */
  bloomThreshold: number;
  /**
   * The miniature look, 0–1: everything above and below a band across the middle of the
   * frame goes out of focus, as if shot through a tilt-shift lens. Tiny Warsaw's, and only
   * for the cameras that look down on the city — never while racing.
   */
  tiltShift: number;
}

export interface Post {
  render(grade: Grade): void;
  /** After `renderer.setSize` or a pixel-ratio change. */
  resize(): void;
  dispose(): void;
}

const BLOOM_RADIUS = 0.4;

/**
 * The tilt-shift: a band in focus across the middle, blurring to `TILT_RADIUS` pixels (at
 * 800 px tall) at the top and bottom of the frame. One pass of a sixteen-tap golden-angle
 * disc whose radius follows the distance from the band, so the cost is one full-screen read
 * per tap and only while a camera asks for it. A depth-aware version — focus on the thing
 * the camera orbits, blur by distance from it — is what Tiny Warsaw uses from its map view;
 * from cameras that look down at an angle, the band is nearly the same picture and needs no
 * depth target.
 */
const TILT_BAND = 0.14;
const TILT_FALLOFF = 0.3;
const TILT_RADIUS = 9;

const TiltShiftShader = {
  name: 'TiltShift',
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    tiltTexel: { value: new THREE.Vector2(1, 1) },
    tiltAmount: { value: 0 },
    tiltRadius: { value: TILT_RADIUS },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform vec2 tiltTexel;
    uniform float tiltAmount;
    uniform float tiltRadius;
    varying vec2 vUv;
    void main() {
      vec4 centre = texture2D( tDiffuse, vUv );
      float blur = tiltAmount * smoothstep( ${TILT_BAND.toFixed(2)}, ${(TILT_BAND + TILT_FALLOFF).toFixed(2)}, abs( vUv.y - 0.5 ) );
      if ( blur < 0.02 ) {
        gl_FragColor = centre;
        return;
      }
      vec4 sum = centre;
      float r = blur * tiltRadius;
      for ( int i = 0; i < 16; i ++ ) {
        float t = ( float( i ) + 0.5 ) / 16.0;
        float a = float( i ) * 2.39996;
        sum += texture2D( tDiffuse, vUv + vec2( cos( a ), sin( a ) ) * sqrt( t ) * r * tiltTexel );
      }
      gl_FragColor = sum / 17.0;
    }
  `,
};

const GRADE_PARS = /* glsl */ `
uniform float gradeExposure;
uniform vec3 gradeBalance;
uniform float gradeContrast;
uniform float gradeSaturation;
uniform float gradeVignette;
uniform vec2 gradeAspect;
`;

const GRADE = /* glsl */ `
	vec3 graded = gl_FragColor.rgb * gradeExposure * gradeBalance;
	const vec3 LUMA = vec3( 0.2126, 0.7152, 0.0722 );
	float gradeLuma = max( dot( graded, LUMA ), 1e-5 );
	// Contrast on luminance, so a hue does not drift as it is pushed.
	float gradeLumaOut = 0.18 * pow( gradeLuma / 0.18, gradeContrast );
	graded *= gradeLumaOut / gradeLuma;
	// Shadows a touch cool, highlights a touch warm: the split every film stock has, and the
	// cheapest way to keep a shadowed street from reading as the same grey as a lit one.
	graded *= mix( vec3( 0.985, 1.0, 1.02 ), vec3( 1.02, 1.0, 0.98 ), smoothstep( 0.04, 0.6, gradeLumaOut ) );
	graded = mix( vec3( dot( graded, LUMA ) ), graded, gradeSaturation );
	vec2 gradeCorner = ( vUv - 0.5 ) * gradeAspect;
	graded *= 1.0 - gradeVignette * smoothstep( 0.35, 1.0, length( gradeCorner ) * 2.0 );
	gl_FragColor.rgb = graded;
`;

export function createPost(
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  camera: THREE.Camera,
): Post {
  const target = new THREE.WebGLRenderTarget(1, 1, { type: THREE.HalfFloatType });
  const composer = new EffectComposer(renderer, target);
  composer.addPass(new RenderPass(scene, camera));
  const bloom = new UnrealBloomPass(new THREE.Vector2(1, 1), 0, BLOOM_RADIUS, 1);
  composer.addPass(bloom);
  const tilt = new ShaderPass(TiltShiftShader);
  tilt.enabled = false;
  composer.addPass(tilt);

  const output = new OutputPass();
  const u = {
    gradeExposure: { value: 1 },
    gradeBalance: { value: new THREE.Vector3(1, 1, 1) },
    gradeContrast: { value: 1 },
    gradeSaturation: { value: 1 },
    gradeVignette: { value: 0 },
    gradeAspect: { value: new THREE.Vector2(1, 1) },
  };
  // The pass's material holds this same object, so adding to it adds to the shader.
  Object.assign(output.uniforms, u);
  output.material.fragmentShader = output.material.fragmentShader
    .replace('varying vec2 vUv;', `varying vec2 vUv;\n${GRADE_PARS}`)
    .replace('// tone mapping', `${GRADE}\n\t\t\t// tone mapping`);
  composer.addPass(output);

  const fxaa = new ShaderPass(FXAAShader);
  composer.addPass(fxaa);

  const size = new THREE.Vector2();
  return {
    render(grade) {
      bloom.enabled = grade.bloomStrength > 0;
      bloom.strength = grade.bloomStrength;
      bloom.threshold = grade.bloomThreshold;
      tilt.enabled = grade.tiltShift > 0.01;
      tilt.uniforms.tiltAmount.value = grade.tiltShift;
      u.gradeExposure.value = grade.exposure;
      u.gradeBalance.value.fromArray(grade.balance);
      // A miniature is a little more saturated and a little more contrasty than the place it
      // is a model of — Tiny Warsaw's 12% and 6% — which is half of why it reads as one.
      u.gradeContrast.value = grade.contrast * (1 + 0.06 * grade.tiltShift);
      u.gradeSaturation.value = grade.saturation * (1 + 0.12 * grade.tiltShift);
      u.gradeVignette.value = grade.vignette + 0.1 * grade.tiltShift;
      composer.render();
    },
    resize() {
      renderer.getSize(size);
      composer.setPixelRatio(renderer.getPixelRatio());
      composer.setSize(size.x, size.y);
      const longest = Math.max(size.x, size.y, 1);
      u.gradeAspect.value.set(size.x / longest, size.y / longest);
      renderer.getDrawingBufferSize(size);
      fxaa.material.uniforms.resolution.value.set(1 / Math.max(1, size.x), 1 / Math.max(1, size.y));
      tilt.uniforms.tiltTexel.value.set(1 / Math.max(1, size.x), 1 / Math.max(1, size.y));
      // The blur is specified at 800 px tall, so it is the same blur at any resolution.
      tilt.uniforms.tiltRadius.value = TILT_RADIUS * (size.y / 800);
    },
    dispose() {
      bloom.dispose();
      tilt.dispose();
      output.dispose();
      fxaa.dispose();
      composer.dispose();
    },
  };
}
